import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getVersionString } from '../../utils/version';
import PropellerLogo from '../../assets/Propeller Travel Logo 448x100 copy.png';
import { isPropellerDomain } from '../../utils/ssoPolicy';

function safeRedirectPath(value: unknown): string {
  const text = typeof value === 'string' ? value : '';
  if (!text || !text.startsWith('/') || text.startsWith('//')) return '/';
  if (text.startsWith('/login') || text.startsWith('/register')) return '/';
  return text;
}

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const { signIn, signInWithGoogle, policyError } = useAuth();

  const isPropellerEmail = useMemo(() => isPropellerDomain(email), [email]);
  const destination = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    if (fromState?.pathname) return safeRedirectPath(`${fromState.pathname}${fromState.search || ''}`);
    const queryRedirect = params.get('redirectTo');
    if (queryRedirect) return safeRedirectPath(queryRedirect);
    return safeRedirectPath(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search, location.state]);

  const destinationLabel = destination === '/ops/inbox' || destination.startsWith('/ops/inbox?')
    ? 'Ops Inbox'
    : destination === '/'
      ? 'the platform'
      : destination.replace(/^\//, '').split(/[/?#]/)[0].replace(/-/g, ' ') || 'the platform';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else if (location.pathname === '/login') {
        navigate(destination, { replace: true });
      }
    } catch (_err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <img
                src={PropellerLogo}
                alt="Propeller Travel"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-gray-600 text-lg">Propeller Travel Management Platform</p>
            <p className="mt-2 text-sm text-blue-700">
              Sign in to continue to {destinationLabel}.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                required
                placeholder="agent@propellertravel.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                required
                placeholder="Enter your password"
                minLength={6}
              />
            </div>

            {(policyError || error) && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {policyError || error}
              </div>
            )}

            <button
              type="button"
              onClick={async () => {
                setError('');
                setLoading(true);
                try {
                  const { error } = await signInWithGoogle(destination);
                  if (error) setError(error.message);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-4 rounded-lg border border-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue with Google
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : 'Sign In'}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500">or</span>
              </div>
            </div>

            {magicLinkSent ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
                Magic link sent to <span className="font-medium">{email}</span>. Open the email on this device and click the link to sign in.
              </div>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  setError('');
                  if (!email) {
                    setError('Enter your email above first, then press “Email me a magic link”.');
                    return;
                  }
                  setLoading(true);
                  try {
                    const { error } = await supabase.auth.signInWithOtp({
                      email,
                      options: {
                        emailRedirectTo: `${window.location.origin}${destination}`,
                      },
                    });
                    if (error) {
                      setError(error.message);
                    } else {
                      setMagicLinkSent(true);
                    }
                  } catch (_err) {
                    setError('Could not send magic link. Try email + password instead.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !email}
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-2.5 px-4 rounded-lg border border-slate-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title={email ? 'Send a one-time sign-in link to this email' : 'Enter your email first'}
              >
                Email me a magic link
              </button>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need an account?{' '}
              <Link to={`/register?redirectTo=${encodeURIComponent(destination)}`} className="text-blue-600 hover:text-blue-700 font-medium">
                Register
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-white text-sm opacity-75">
            Multi-GDS PNR Management & RTW Planning
          </p>
          <p className="text-white text-xs opacity-60">
            {getVersionString()}
          </p>
        </div>
      </div>
    </div>
  );
}
